import type { 
  BirhausTableColumn, 
  BirhausBulkAction, 
  DataTableMetrics,
  TableConfigFactory 
} from './types'
import { createSimpleAction } from '../../types/bulk-actions'

/**
 * BIRHAUS DataTable Utilities
 * Cognitive load validation and configuration helpers
 */

// BIRHAUS validation constants
const MILLER_LAW_MAX_COLUMNS = 7
const COGNITIVE_LOAD_THRESHOLDS = {
  LOW: 5,
  MEDIUM: 7,
  HIGH: 10
}
const BIRHAUS_4_3_1_MAX_ACTIONS = 4

/**
 * Validates cognitive load based on BIRHAUS principles
 */
export function validateCognitiveLoad<T>(
  columns: BirhausTableColumn<T>[],
  bulkActions: BirhausBulkAction<T>[] = []
): DataTableMetrics {
  const columnsCount = columns.length
  const bulkActionsCount = bulkActions.length
  
  // Calculate total cognitive load
  const totalLoad = columnsCount + bulkActionsCount
  
  let cognitiveLoad: 'low' | 'medium' | 'high' = 'low'
  if (totalLoad > COGNITIVE_LOAD_THRESHOLDS.HIGH) {
    cognitiveLoad = 'high'
  } else if (totalLoad > COGNITIVE_LOAD_THRESHOLDS.MEDIUM) {
    cognitiveLoad = 'medium'
  }
  
  // Log warnings for BIRHAUS violations
  if (columnsCount > MILLER_LAW_MAX_COLUMNS) {
    console.warn(
      `BIRHAUS DataTable: ${columnsCount} columns exceed Miller's Law recommendation of ${MILLER_LAW_MAX_COLUMNS}. ` +
      'Consider grouping columns or using progressive disclosure.'
    )
  }
  
  if (bulkActionsCount > BIRHAUS_4_3_1_MAX_ACTIONS) {
    console.warn(
      `BIRHAUS DataTable: ${bulkActionsCount} bulk actions exceed 4-3-1 rule of ${BIRHAUS_4_3_1_MAX_ACTIONS}. ` +
      'Consider consolidating actions or using a dropdown menu.'
    )
  }
  
  return {
    columnsCount,
    rowsPerPage: 10, // Default from component
    bulkActionsCount,
    cognitiveLoad
  }
}

/**
 * Factory function to create BIRHAUS-compliant table columns
 */
export function createBirhausTableColumn<T>(
  key: string,
  labelEs: string,
  labelEn?: string,
  options: Partial<Omit<BirhausTableColumn<T>, 'key' | 'labelEs' | 'labelEn'>> = {}
): BirhausTableColumn<T> {
  return {
    key,
    labelEs,
    labelEn: labelEn || labelEs,
    sortable: true,
    searchable: true,
    ...options
  }
}

/**
 * Factory function to create BIRHAUS-compliant bulk actions
 * @deprecated Use createSimpleAction or createUndoableAction from central types instead
 */
export function createBirhausBulkAction<T>(
  labelEs: string,
  onClick: (items: T[]) => void | Promise<void>,
  labelEn?: string,
  options: Partial<Omit<BirhausBulkAction<T>, 'labelEs' | 'labelEn' | 'onClick'>> = {}
): BirhausBulkAction<T> {
  return createSimpleAction<T>({
    id: `action-${Date.now()}`, // Generate unique ID
    labelEs,
    labelEn: labelEn || labelEs,
    onClick,
    variant: options.variant || 'secondary',
    ...options
  })
}

/**
 * Creates a complete table configuration factory
 */
export function createTableConfigFactory<T>(): TableConfigFactory<T> {
  return {
    createColumn: (config) => createBirhausTableColumn(
      config.key,
      config.labelEs,
      config.labelEn,
      config
    ),
    createBulkAction: (config) => {
      // Type guard to handle discriminated union
      const onClick = config.onClick as (items: T[]) => void | Promise<void>
      return createBirhausBulkAction(
        config.labelEs,
        onClick,
        config.labelEn,
        config
      )
    },
    validateCognitiveLoad
  }
}

/**
 * Utility to sort data with nested key support
 */
export function sortDataByColumn<T>(
  data: T[],
  columnKey: string,
  direction: 'asc' | 'desc'
): T[] {
  return [...data].sort((a, b) => {
    const aValue = columnKey.includes('.')
      ? columnKey.split('.').reduce((obj: any, key) => obj?.[key], a)
      : (a as any)[columnKey]
    const bValue = columnKey.includes('.')
      ? columnKey.split('.').reduce((obj: any, key) => obj?.[key], b)
      : (b as any)[columnKey]

    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    if (aValue < bValue) return direction === 'asc' ? -1 : 1
    if (aValue > bValue) return direction === 'asc' ? 1 : -1
    return 0
  })
}

/**
 * Filter data based on search term and searchable columns
 */
export function filterDataBySearch<T>(
  data: T[],
  searchTerm: string,
  columns: BirhausTableColumn<T>[]
): T[] {
  if (!searchTerm) return data
  
  const searchableColumns = columns.filter(col => col.searchable !== false)
  const term = searchTerm.toLowerCase()

  return data.filter(item => {
    return searchableColumns.some(col => {
      const value = col.key.includes('.')
        ? col.key.split('.').reduce((obj: any, key) => obj?.[key], item)
        : (item as any)[col.key]
      
      return value?.toString().toLowerCase().includes(term)
    })
  })
}

/**
 * Calculate pagination info
 */
export function calculatePagination(
  totalCount: number,
  currentPage: number,
  pageSize: number
) {
  const totalPages = Math.ceil(totalCount / pageSize)
  
  return {
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    startIndex: (currentPage - 1) * pageSize,
    endIndex: Math.min(currentPage * pageSize - 1, totalCount - 1)
  }
}

/**
 * BIRHAUS-compliant page range calculation (Miller's Law)
 */
export function calculateVisiblePages(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 7
): number[] {
  const actualMax = Math.min(maxVisible, totalPages)
  
  if (totalPages <= actualMax) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  
  const halfVisible = Math.floor(actualMax / 2)
  let start = currentPage - halfVisible
  let end = currentPage + halfVisible
  
  if (start < 1) {
    start = 1
    end = actualMax
  } else if (end > totalPages) {
    start = totalPages - actualMax + 1
    end = totalPages
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

/**
 * Export data formatting utilities
 */
export function formatDataForExport<T>(
  data: T[],
  columns: BirhausTableColumn<T>[],
  format: 'csv' | 'json' = 'csv'
) {
  if (format === 'json') {
    return JSON.stringify(data, null, 2)
  }
  
  // CSV format
  const headers = columns.map(col => col.labelEs).join(',')
  const rows = data.map(item => 
    columns.map(col => {
      const value = col.key.includes('.')
        ? col.key.split('.').reduce((obj: any, key) => obj?.[key], item)
        : (item as any)[col.key]
      
      // Escape CSV values
      const stringValue = String(value || '')
      return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
        ? `"${stringValue.replace(/"/g, '""')}"` 
        : stringValue
    }).join(',')
  ).join('\n')
  
  return `${headers}\n${rows}`
}

// Pre-configured column types for common use cases
export const commonColumnTypes = {
  text: (key: string, labelEs: string, labelEn?: string) =>
    createBirhausTableColumn(key, labelEs, labelEn),
    
  number: (key: string, labelEs: string, labelEn?: string) =>
    createBirhausTableColumn(key, labelEs, labelEn, {
      className: 'text-right font-mono'
    }),
    
  date: (key: string, labelEs: string, labelEn?: string) =>
    createBirhausTableColumn(key, labelEs, labelEn, {
      render: (item: any, index) => {
        const value = item[key]
        if (!value) return '-'
        return new Date(value).toLocaleDateString('es-PY')
      }
    }),
    
  currency: (key: string, labelEs: string, labelEn?: string) =>
    createBirhausTableColumn(key, labelEs, labelEn, {
      className: 'text-right font-mono',
      render: (item: any, index) => {
        const value = item[key]
        if (!value && value !== 0) return '-'
        return new Intl.NumberFormat('es-PY', {
          style: 'currency',
          currency: 'PYG',
          minimumFractionDigits: 0
        }).format(value)
      }
    }),
    
  boolean: (key: string, labelEs: string, labelEn?: string) =>
    createBirhausTableColumn(key, labelEs, labelEn, {
      className: 'text-center',
      render: (item: any, index) => {
        const value = item[key]
        return value ? '✅' : '❌'
      }
    })
}